import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { 
  Sparkles, 
  Image as ImageIcon, 
  Video, 
  ExternalLink,
  ChevronRight,
  Calendar,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";

export default function Spotlight() {
  const [deliveredMedia, setDeliveredMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeliveredMedia();
  }, []);

  const fetchDeliveredMedia = async () => {
    try {
      // Get recently delivered programs (last 14 days)
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      // Get delivered programs
      const res = await apiClient.get(`/programs?stage=Delivered&page_size=20`);
      const programs = res.data.data || res.data || [];
      
      // Filter to recently delivered and get deliverables
      const recentlyDelivered = programs.filter(p => {
        const updatedAt = new Date(p.updated_at || p.last_activity_at);
        return updatedAt >= fourteenDaysAgo;
      });

      // Get deliverables for these programs
      const mediaItems = [];
      for (const program of recentlyDelivered.slice(0, 5)) {
        try {
          const delivRes = await apiClient.get(`/programs/${program.id}/deliverables`);
          const deliverables = delivRes.data || [];
          
          // Get completed deliverables
          const completedDelivs = deliverables.filter(d => 
            d.status === 'Delivered' || d.status === 'Complete'
          );
          
          if (completedDelivs.length > 0) {
            mediaItems.push({
              id: program.id,
              programName: program.program_name,
              clientName: program.client_name,
              deliveredAt: program.updated_at || program.last_activity_at,
              deliverableCount: completedDelivs.length,
              deliverableTypes: [...new Set(completedDelivs.map(d => d.deliverable_type))],
              coverImageId: program.cover_image_id
            });
          }
        } catch (err) {
          // Skip if can't fetch deliverables
          console.error(`Failed to fetch deliverables for program ${program.id}`, err);
        }
      }
      
      setDeliveredMedia(mediaItems);
    } catch (err) {
      console.error("Failed to fetch delivered media", err);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDeliverableIcon = (types) => {
    if (types.some(t => t?.toLowerCase().includes('animation') || t?.toLowerCase().includes('video'))) {
      return <Video className="w-4 h-4 text-purple-500" />;
    }
    return <ImageIcon className="w-4 h-4 text-blue-500" />;
  };

  const getDeliverableBadgeColor = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower.includes('exterior')) return 'bg-green-100 text-green-700 border-green-200';
    if (typeLower.includes('interior')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (typeLower.includes('animation')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (typeLower.includes('360')) return 'bg-pink-100 text-pink-700 border-pink-200';
    if (typeLower.includes('aerial')) return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return null; // Don't show loading, just hide until ready
  }

  if (deliveredMedia.length === 0) {
    return null; // Don't show if no delivered media
  }

  return (
    <Card 
      data-testid="spotlight" 
      className="border-border overflow-hidden"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-purple-100">
              <Sparkles className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-purple-800">
                Spotlight
              </CardTitle>
              <p className="text-[10px] text-purple-600">Recently published</p>
            </div>
          </div>
          <Badge className="rounded-full bg-purple-100 text-purple-700 border-purple-200">
            {deliveredMedia.length} project{deliveredMedia.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-1">
        {deliveredMedia.map((item) => (
          <Link
            key={item.id}
            to={`/programs/${item.id}`}
            className="flex items-start gap-3 py-3 px-1 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
            data-testid={`spotlight-item-${item.id}`}
          >
            {/* Thumbnail placeholder or icon */}
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              {getDeliverableIcon(item.deliverableTypes)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate group-hover:text-purple-700 transition-colors">
                    {item.programName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{item.clientName}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-purple-300 group-hover:text-purple-500 transition-colors flex-shrink-0 mt-0.5" />
              </div>
              
              {/* Deliverable types */}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {item.deliverableTypes.slice(0, 3).map((type, idx) => (
                  <Badge 
                    key={idx}
                    variant="outline" 
                    className={`text-[10px] px-1.5 py-0 ${getDeliverableBadgeColor(type)}`}
                  >
                    {type}
                  </Badge>
                ))}
                {item.deliverableTypes.length > 3 && (
                  <span className="text-[10px] text-muted-foreground">
                    +{item.deliverableTypes.length - 3} more
                  </span>
                )}
              </div>
              
              {/* Meta info */}
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(item.deliveredAt)}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {item.deliverableCount} deliverable{item.deliverableCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </Link>
        ))}
        
        <Link
          to="/programs?stage=Delivered"
          className="block text-center text-xs text-purple-600 hover:text-purple-800 pt-2 transition-colors"
        >
          View all delivered projects
        </Link>
      </CardContent>
    </Card>
  );
}
